import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BsCheckLg } from "react-icons/bs";
import {
  getSellerWalletsByCompanyId,
  transferSellerWalletFunds,
} from "../../../store/actions/fundManagement";
import { formatNumberWithCommas } from "../../../config/utils";
import InputCalculator from "../../common/InputCalculator";
import { normalizeCalculatorValue } from "../../common/calculatorValue";
import "./transferFunds.css";

const STEP_FORM = "form";
const STEP_CONFIRM = "confirm";
const STEP_SUCCESS = "success";

const NOTES_MAX_LENGTH = 500;
const REQUIRED_FIELD_MSG = "This is a mandatory field.";
const TRANSFER_ERROR_MSG = "Fund transfer could not be completed.";

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return `$${formatNumberWithCommas(num.toFixed(2))}`;
};

const buildSellerOption = (seller) => ({
  value: seller.creditCompanySellerId ?? seller.sellerId ?? seller.id,
  sellerId: seller.sellerId,
  companySellerId: seller.creditCompanySellerId,
  companyId: seller.companyId,
  sellerName: seller.sellerName,
  availableAmount: Number(seller.availableAmount || 0),
  label: `${seller.sellerName} (${formatCurrency(seller.availableAmount)})`,
});

const resolveRoleName = (userValue) => {
  if (userValue?.Value?.roleName) return userValue.Value.roleName;
  if (userValue?.Value?.isSuperAdmin) return "Super Admin";
  if (userValue?.Value?.isAdmin) return "Company Admin";
  return "User";
};

const getPerformedByLabel = (userValue) => {
  const roleName = resolveRoleName(userValue);
  const company = userValue?.Value?.companyName;
  return company ? `${roleName} (${company})` : roleName;
};

// Filters a seller entry against the currently-selected company. For a
// Company Admin the fundList is already company-scoped, so we accept every
// record. For a Super Admin we match on companyId first, then companyName.
export const matchesSelectedCompany = (record, selectedCompany, isSuperAdmin) => {
  if (!selectedCompany?.value && !selectedCompany?.label) return true;
  if (!isSuperAdmin) return true;
  if (record?.companyId && selectedCompany?.value) {
    return record.companyId === selectedCompany.value;
  }
  if (record?.companyName && selectedCompany?.label) {
    return record.companyName === selectedCompany.label;
  }
  return false;
};

const dedupeSellerOptions = (records) => {
  const seen = new Set();
  const options = [];
  records.forEach((seller) => {
    const option = buildSellerOption(seller);
    const key = option.sellerId || option.value;
    if (!key || seen.has(key)) return;
    seen.add(key);
    options.push(option);
  });
  return options;
};

export const getCheckedRows = (fundList) => (fundList || []).filter((row) => row?.isChecked);

// The company shared by every checked row, or null when none/mixed/unknown.
export const getSharedCheckedCompany = (checkedRows) => {
  const first = checkedRows?.[0];
  if (!first?.companyId) return null;
  const isShared = checkedRows.every((row) => row.companyId === first.companyId);
  return isShared ? { value: first.companyId, label: first.companyName } : null;
};

// With 2+ checked rows only those sellers are offered; 0 or 1 keeps the full company list.
export const restrictToCheckedSellers = (records, checkedRows) => {
  if ((checkedRows?.length || 0) < 2) return records;
  const checkedSellerIds = new Set(checkedRows.map((row) => row.sellerId));
  return records.filter((record) => checkedSellerIds.has(record?.sellerId));
};

export const buildTransferPayload = (formData, userValue) => {
  const amount = Number(formData.amount);
  const parentCompanyId =
    Number(formData.company?.value) ||
    Number(formData.fromSeller?.companyId) ||
    Number(formData.toSeller?.companyId) ||
    0;
  return {
    parentCompanyId,
    fromSellerId: Number(formData.fromSeller?.sellerId) || 0,
    toSellerId: Number(formData.toSeller?.sellerId) || 0,
    amount,
    description: formData.notes || "",
    attachmentUrl: "",
    userId: Number(userValue?.Value?.employeeId) || 0,
    tenantId: Number(userValue?.Value?.tenantId) || 0,
    userRole: userValue?.Value?.roleName || userValue?.Value?.role || "",
  };
};

const buildSuccessData = (entity, formData, amount) => ({
  transferReferenceId: entity.TransferReferenceId || "",
  debitTransactionId: entity.DebitTransactionId,
  creditTransactionId: entity.CreditTransactionId,
  fromBalanceAfter:
    entity.FromSellerNewBalance ??
    formData.fromSeller.availableAmount - amount,
  toBalanceAfter:
    entity.ToSellerNewBalance ??
    formData.toSeller.availableAmount + amount,
  amount,
});

export const collectValidationErrors = (formData, isSuperAdmin) => {
  const errs = {};
  if (isSuperAdmin && !formData.company?.value) {
    errs.company = "Please select a company.";
  }
  if (!formData.fromSeller) errs.fromSeller = REQUIRED_FIELD_MSG;
  if (!formData.toSeller) errs.toSeller = REQUIRED_FIELD_MSG;
  if (
    formData.fromSeller &&
    formData.toSeller &&
    formData.fromSeller.value === formData.toSeller.value
  ) {
    errs.toSeller = "Debit and credit wallets cannot be the same.";
  }
  const amt = Number(formData.amount);
  const amountInvalid = !formData.amount || Number.isNaN(amt) || amt <= 0;
  if (amountInvalid) {
    errs.amount = "Enter an amount greater than 0.";
  } else if (formData.fromSeller && amt > formData.fromSeller.availableAmount) {
    errs.amount = "Amount cannot exceed the debit wallet balance.";
  }
  return errs;
};

function TransferFundsModal(props) {
  const {
    show,
    onHide,
    activeSellerList = [],
    fundList = [],
    onTransferComplete,
  } = props;

  const userValue = useSelector((state) => state?.auth?.user);
  const modalRef = useRef();

  const [step, setStep] = useState(STEP_FORM);
  const [extraSellerOptions, setExtraSellerOptions] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [successData, setSuccessData] = useState(null);

  const isSuperAdmin = Boolean(userValue?.Value?.isSuperAdmin);

  const checkedRows = useMemo(() => getCheckedRows(fundList), [fundList]);
  const sharedCheckedCompany = useMemo(() => getSharedCheckedCompany(checkedRows), [checkedRows]);
  const sharedCompanyValue = sharedCheckedCompany?.value;
  const sharedCompanyLabel = sharedCheckedCompany?.label;
  // Keyed on primitives so the reference only changes when the company really changes.
  const checkedCompanyOption = useMemo(
    () => (sharedCompanyValue ? { value: sharedCompanyValue, label: sharedCompanyLabel } : null),
    [sharedCompanyValue, sharedCompanyLabel]
  );

  const companyOptions = useMemo(() => {
    if (!isSuperAdmin) return [];
    const checkedCompanyIds = new Set(checkedRows.map((row) => row.companyId));
    const allCompanies = (activeSellerList || [])
      .map((c) => ({ value: c.Id, label: c.name }))
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return checkedRows.length > 1
      ? allCompanies.filter((c) => checkedCompanyIds.has(c.value))
      : allCompanies;
  }, [activeSellerList, isSuperAdmin, checkedRows]);

  const initialCompany = useMemo(() => {
    if (isSuperAdmin) {
      // Prefilled only when the checked rows belong to one company; otherwise the Super Admin picks.
      return checkedCompanyOption;
    }
    // Some sessions don't carry companyId in auth state; fall back to the
    // companyId stamped on any fundList entry (they all belong to the
    // logged-in user's company).
    const fallbackCompanyId = (fundList || []).find(
      (f) => f?.companyId
    )?.companyId;
    return {
      value: userValue?.Value?.companyId || fallbackCompanyId || 0,
      label:
        userValue?.Value?.companyName ||
        (fundList || [])[0]?.companyName ||
        "",
    };
  }, [fundList, isSuperAdmin, userValue, checkedCompanyOption]);

  const [formData, setFormData] = useState({
    company: initialCompany,
    fromSeller: null,
    toSeller: null,
    amount: "",
    notes: "",
  });

  useEffect(() => {
    setFormData((prev) => ({ ...prev, company: initialCompany }));
  }, [initialCompany]);

  // Build dropdown options from the sellers already shown on the parent
  // Fund Management screen (guaranteed to be in-scope for the user) and
  // merge with any additional sellers returned by the backend refresh.
  const sellerOptions = useMemo(() => {
    const selectedCompany = formData.company || initialCompany;
    const inScope = (record) =>
      matchesSelectedCompany(record, selectedCompany, isSuperAdmin);
    const scoped = [
      ...(fundList || []).filter(inScope),
      ...(extraSellerOptions || []).filter(inScope),
    ];
    return dedupeSellerOptions(restrictToCheckedSellers(scoped, checkedRows));
  }, [fundList, extraSellerOptions, formData.company, initialCompany, isSuperAdmin, checkedRows]);

  const refreshSellersFromApi = useCallback(async (companyId) => {
    if (!companyId) {
      setExtraSellerOptions([]);
      return;
    }
    setLoadingSellers(true);
    try {
      const resp = await getSellerWalletsByCompanyId(companyId, userValue);
      const list =
        resp?.data?.Entity?.listFundMasterOrdersEntity ||
        resp?.data?.EntityList ||
        [];
      setExtraSellerOptions(list);
    } finally {
      setLoadingSellers(false);
    }
  }, [userValue]);

  useEffect(() => {
    if (show && formData.company?.value) {
      refreshSellersFromApi(formData.company.value).catch((e) => {
        // fundList-derived options stay usable, so a failed refresh needs no user-facing error.
        setExtraSellerOptions([]);
        // eslint-disable-next-line no-console
        console.warn("Transfer Funds: seller refresh failed", e?.message);
      });
    }
  }, [show, formData.company?.value, refreshSellersFromApi]);

  // Show every seller of the company in the Debit dropdown, but mark those
  // with zero/negative balance as ineligible so users can see they exist.
  const fromSellerOptions = useMemo(
    () =>
      sellerOptions.map((s) => ({
        ...s,
        isDisabled: s.availableAmount <= 0,
        label:
          s.availableAmount > 0
            ? s.label
            : `${s.label} — not eligible`,
      })),
    [sellerOptions]
  );

  const toSellerOptions = useMemo(() => {
    if (!formData.fromSeller) return [];
    return sellerOptions.filter((s) => s.value !== formData.fromSeller.value);
  }, [sellerOptions, formData.fromSeller]);

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleCompanyChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      company: option,
      fromSeller: null,
      toSeller: null,
    }));
    clearFieldError("company");
  };

  const handleFromSellerChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      fromSeller: option,
      toSeller:
        prev.toSeller && option && prev.toSeller.value === option.value
          ? null
          : prev.toSeller,
    }));
    clearFieldError("fromSeller");
  };

  const handleToSellerChange = (option) => {
    setFormData((prev) => ({ ...prev, toSeller: option }));
    clearFieldError("toSeller");
  };

  const handleAmountChange = (e) => {
    const raw = normalizeCalculatorValue(e);
    if (raw === null) return;
    // A plain number keeps the two-decimal rule; anything else is a calculator expression.
    if (/^[\d.]*$/.test(raw) && !/^\d*\.?\d{0,2}$/.test(raw)) return;
    setFormData((prev) => ({ ...prev, amount: raw }));
    clearFieldError("amount");
  };

  const handleNotesChange = (e) => {
    setFormData((prev) => ({ ...prev, notes: e.target.value }));
  };

  const validateForm = () => {
    const next = collectValidationErrors(formData, isSuperAdmin);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleReview = () => {
    if (validateForm()) setStep(STEP_CONFIRM);
  };

  const showTransferError = (msg) => {
    toast.error(msg || TRANSFER_ERROR_MSG);
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    const amount = Number(formData.amount);
    const payload = buildTransferPayload(formData, userValue);
    try {
      const resp = await transferSellerWalletFunds(payload);
      const body = resp?.data || {};
      const entity = body.Entity || {};
      if (body.Status && body.Status !== 200) {
        showTransferError(body?.Message?.AppStatusDescription);
        return;
      }
      if (entity.WasDuplicate) {
        toast.info("This transfer was already processed. Showing the existing record.");
      }
      setSuccessData(buildSuccessData(entity, formData, amount));
      setStep(STEP_SUCCESS);
    } catch (e) {
      const apiMsg =
        e?.response?.data?.Message?.AppStatusDescription ||
        e?.response?.data?.errorDescription;
      showTransferError(apiMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (step === STEP_SUCCESS && typeof onTransferComplete === "function") {
      onTransferComplete();
    }
    setStep(STEP_FORM);
    setFormData({
      company: initialCompany,
      fromSeller: null,
      toSeller: null,
      amount: "",
      notes: "",
    });
    setErrors({});
    setSuccessData(null);
    onHide();
  };

  const renderFormStep = () => {
    const needsCompanyFirst = isSuperAdmin && !formData.company?.value;
    let debitPlaceholder = "Select seller wallet";
    if (loadingSellers) debitPlaceholder = "Loading sellers...";
    else if (needsCompanyFirst) debitPlaceholder = "Select seller";
    const debitHint = needsCompanyFirst
      ? "Select company first."
      : "Sellers with zero or negative balance are not eligible.";
    return (
    <>
      <div className="transfer-modal-body">
        <div className="transfer-form-full">
          <label className="transfer-label" htmlFor="tf-company">Company</label>
          {isSuperAdmin ? (
            <Select
              inputId="tf-company"
              options={companyOptions}
              value={formData.company}
              onChange={handleCompanyChange}
              placeholder="Select company"
              isDisabled={!companyOptions.length}
              classNamePrefix="tf-select"
            />
          ) : (
            <input
              id="tf-company"
              type="text"
              className="transfer-input"
              value={formData.company?.label || ""}
              disabled
            />
          )}
          {errors.company && <div className="transfer-error">{errors.company}</div>}
        </div>

        <div className="transfer-form-row">
          <div className="transfer-form-col">
            <label className="transfer-label" htmlFor="tf-from">
              Debit Wallet<span className="transfer-required">*</span>
            </label>
            <Select
              inputId="tf-from"
              options={fromSellerOptions}
              value={formData.fromSeller}
              onChange={handleFromSellerChange}
              placeholder={debitPlaceholder}
              isDisabled={
                loadingSellers ||
                (isSuperAdmin && !formData.company?.value)
              }
              classNamePrefix="tf-select"
              noOptionsMessage={() =>
                loadingSellers ? "Loading..." : "No eligible sellers"
              }
            />
            {formData.fromSeller ? (
              <div className="transfer-balance-info">
                Available balance: <strong>{formatCurrency(formData.fromSeller.availableAmount)}</strong>
              </div>
            ) : (
              <div className="transfer-hint">
                {debitHint}
              </div>
            )}
            {errors.fromSeller && <div className="transfer-error">{errors.fromSeller}</div>}
          </div>

          <div className="transfer-form-col">
            <label className="transfer-label" htmlFor="tf-to">
              Credit Wallet<span className="transfer-required">*</span>
            </label>
            <Select
              inputId="tf-to"
              options={toSellerOptions}
              value={formData.toSeller}
              onChange={handleToSellerChange}
              placeholder={
                formData.fromSeller ? "Select seller wallet" : "Select debit wallet first"
              }
              isDisabled={!formData.fromSeller}
              classNamePrefix="tf-select"
            />
            {formData.toSeller ? (
              <div className="transfer-balance-info">
                Current balance: <strong>{formatCurrency(formData.toSeller.availableAmount)}</strong>
              </div>
            ) : (
              <div className="transfer-hint">
                Please select the debit wallet first.
              </div>
            )}
            {errors.toSeller && <div className="transfer-error">{errors.toSeller}</div>}
          </div>
        </div>

        <div className="transfer-form-full">
          <label className="transfer-label" htmlFor="tf-amount">
            Amount to Transfer<span className="transfer-required">*</span>
          </label>
          <InputCalculator
            id="tf-amount"
            inputMode="decimal"
            className="transfer-input"
            placeholder="$ 0.00"
            maxLength={50}
            value={String(formData.amount ?? '')}
            onChange={handleAmountChange}
            autoComplete="off"
            percentMode
          />
          {errors.amount && <div className="transfer-error">{errors.amount}</div>}
        </div>

        <div className="transfer-form-full">
          <label className="transfer-label" htmlFor="tf-notes">Notes</label>
          <textarea
            id="tf-notes"
            className="transfer-textarea"
            placeholder="Provide a reason for this transfer"
            maxLength={NOTES_MAX_LENGTH}
            value={formData.notes}
            onChange={handleNotesChange}
          />
        </div>
      </div>
      <div className="transfer-modal-footer">
        <button type="button" className="btn-secondary-transfer" onClick={handleClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary-transfer"
          onClick={handleReview}
        >
          Review Transfer
        </button>
      </div>
    </>
    );
  };

  const renderConfirmStep = () => {
    const amt = Number(formData.amount);
    const fromCurrent = formData.fromSeller?.availableAmount || 0;
    const toCurrent = formData.toSeller?.availableAmount || 0;
    return (
      <>
        <div className="transfer-modal-body">
          <div className="transfer-confirm-grid">
            <div className="transfer-confirm-card">
              <div className="transfer-confirm-card-label">Debit from</div>
              <div className="transfer-confirm-card-seller">
                {formData.fromSeller?.sellerName}
              </div>
              <div className="transfer-confirm-row">
                <span>Current</span>
                <span>{formatCurrency(fromCurrent)}</span>
              </div>
              <div className="transfer-confirm-row">
                <span>After transfer</span>
                <span className="transfer-confirm-after-debit">
                  {formatCurrency(fromCurrent - amt)}
                </span>
              </div>
            </div>
            <div className="transfer-confirm-arrow" aria-hidden="true">
              <span>→</span>
              <span className="transfer-confirm-arrow-amount">
                {formatCurrency(amt)}
              </span>
            </div>
            <div className="transfer-confirm-card">
              <div className="transfer-confirm-card-label">Credit to</div>
              <div className="transfer-confirm-card-seller">
                {formData.toSeller?.sellerName}
              </div>
              <div className="transfer-confirm-row">
                <span>Current</span>
                <span>{formatCurrency(toCurrent)}</span>
              </div>
              <div className="transfer-confirm-row">
                <span>After transfer</span>
                <span className="transfer-confirm-after-credit">
                  {formatCurrency(toCurrent + amt)}
                </span>
              </div>
            </div>
          </div>

          <div className="transfer-confirm-meta">
            <div className="transfer-confirm-meta-row">
              <div className="transfer-confirm-meta-label">Company</div>
              <div className="transfer-confirm-meta-value">
                {formData.company?.label}
              </div>
            </div>
            <div className="transfer-confirm-meta-row">
              <div className="transfer-confirm-meta-label">Notes</div>
              <div className="transfer-confirm-meta-value">
                {formData.notes?.trim() || "—"}
              </div>
            </div>
            <div className="transfer-confirm-meta-row">
              <div className="transfer-confirm-meta-label">Performed by</div>
              <div className="transfer-confirm-meta-value">
                {getPerformedByLabel(userValue)}
              </div>
            </div>
          </div>

          <p className="transfer-confirm-note">
            Transfers are posted immediately to both seller ledgers and cannot be
            edited. To reverse a transfer, initiate a new transfer in the opposite
            direction.
          </p>
        </div>
        <div className="transfer-modal-footer">
          <button
            type="button"
            className="btn-secondary-transfer"
            onClick={() => setStep(STEP_FORM)}
            disabled={isSubmitting}
          >
            Back
          </button>
          <button
            type="button"
            className="btn-primary-transfer"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Processing..." : "Confirm Transfer"}
          </button>
        </div>
      </>
    );
  };

  const renderSuccessStep = () => {
    const amt = successData?.amount || Number(formData.amount);
    return (
      <>
        <div className="transfer-modal-body">
          <div className="transfer-success-header">
            <div className="transfer-success-icon">
              <BsCheckLg />
            </div>
            <div>
              <h4 className="transfer-success-title">Funds transferred</h4>
              <p className="transfer-success-message">
                {formatCurrency(amt)} has been transferred from{" "}
                <strong>{formData.fromSeller?.sellerName}</strong> to{" "}
                <strong>{formData.toSeller?.sellerName}</strong> ({formData.company?.label}).
                Transfer Reference ID: {successData?.transferReferenceId}
              </p>
            </div>
          </div>
          <table className="transfer-success-table">
            <thead>
              <tr>
                <th>Seller</th>
                <th>Transaction Type</th>
                <th>Dr/Cr</th>
                <th>Amount</th>
                <th>Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{formData.fromSeller?.sellerName}</td>
                <td>Wallet Transfer</td>
                <td><span className="badge-debit">Debit</span></td>
                <td>{formatCurrency(amt)}</td>
                <td>{formatCurrency(successData?.fromBalanceAfter)}</td>
                <td>Completed</td>
              </tr>
              <tr>
                <td>{formData.toSeller?.sellerName}</td>
                <td>Wallet Transfer</td>
                <td><span className="badge-credit">Credit</span></td>
                <td>{formatCurrency(amt)}</td>
                <td>{formatCurrency(successData?.toBalanceAfter)}</td>
                <td>Completed</td>
              </tr>
            </tbody>
          </table>
          <p className="transfer-success-footer-note">
            Both entries appear in the Seller Ledger with the same Transfer Reference
            ID and notes.
          </p>
        </div>
        <div className="transfer-modal-footer">
          <button
            type="button"
            className="btn-primary-transfer"
            onClick={handleClose}
          >
            Done
          </button>
        </div>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      className="transferFundsModal"
      size="lg"
      ref={modalRef}
    >
      <div className="transfer-modal-header">
        <div>
          <h3 className="transfer-modal-title">
            {step === STEP_CONFIRM ? "Confirm Transfer" : "Transfer Funds"}
          </h3>
          <p className="transfer-modal-subtitle">
            Transfer available balance from one seller's wallet to another within the same company.
          </p>
        </div>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleClose}
        />
      </div>
      {step === STEP_FORM && renderFormStep()}
      {step === STEP_CONFIRM && renderConfirmStep()}
      {step === STEP_SUCCESS && renderSuccessStep()}
    </Modal>
  );
}

TransferFundsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  activeSellerList: PropTypes.array,
  fundList: PropTypes.array,
  onTransferComplete: PropTypes.func,
};

export default TransferFundsModal;
